import React, { useEffect, useState, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/msalConfig';
import { AccountInfo } from '@azure/msal-browser';
import axios from 'axios';
import { env } from '../../config/env';
import { useAuth, User } from '../../context/AuthContext';

interface AuthHandlerProps {
  children: React.ReactNode;
}

export const AuthHandler: React.FC<AuthHandlerProps> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { currentUser, setUser } = useAuth();
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [userBootstrapped, setUserBootstrapped] = useState<boolean>(false);
  const redirectStarted = useRef(false);

  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      try {
        if (inProgress !== 'none') return;

        const accounts: AccountInfo[] = instance.getAllAccounts();

        if (accounts.length > 0) {
          const activeAccount: AccountInfo | null = accounts[0] ?? null;
          instance.setActiveAccount(activeAccount);
        }

        try {
          const response = await instance.handleRedirectPromise();
          if (response?.account) {
            instance.setActiveAccount(response.account);
          }
        } catch (error: unknown) {
          console.error('Handle redirect error:', error);
        }

        setIsInitializing(false);
      } catch (error: unknown) {
        console.error('Auth initialization error:', error);
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [instance, inProgress]);

  useEffect(() => {
    if (currentUser && !userBootstrapped) {
      setUserBootstrapped(true);
      return;
    }

    const bootstrapUser = async (): Promise<void> => {
      try {
        const accounts: AccountInfo[] = instance.getAllAccounts();
        if (accounts.length === 0) {
          return;
        }

        const account: AccountInfo | undefined = accounts[0];

        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });

        if (!tokenResponse?.accessToken) {
          return;
        }

        const email = account.username;

        const employeeIdResponse = await axios.get(
          `/api/employees/by-email/${encodeURIComponent(email)}`,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
            baseURL: env.API_BASE_URL,
          }
        );

        const employeeId = employeeIdResponse.data;

        const employeeResponse = await axios.get(
          `/api/employees/${employeeId}`,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
            baseURL: env.API_BASE_URL,
          }
        );

        const employeeData = employeeResponse.data;
        const getRoleFromRoleName = (roleName: string): 'respondent' | 'creator' | 'admin' => {
          const roleMap: Record<string, 'respondent' | 'creator' | 'admin'> = {
            'FeedbackTaker': 'respondent',
            'FeedbackCreator': 'creator',
            'Admin': 'admin',
            'Administrator': 'admin',
          };
          return roleMap[roleName] || 'respondent';
        };

        const role = employeeData.roleName ? getRoleFromRoleName(employeeData.roleName) : 'respondent';

        const user: User = {
          id: String(employeeData.id),
          email: employeeData.email,
          name: employeeData.fullName.split(' ')[0] || '',
          fullName: employeeData.fullName,
          role,
          roleName: employeeData.roleName || 'FeedbackTaker',
        };

        setUser(user);
        setUserBootstrapped(true);
      } catch (error: unknown) {
        console.error('User bootstrap failed:', error);

        const accounts: AccountInfo[] = instance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          const fallbackUser: User = {
            id: '',  // No valid CoreAPI ID available
            email: account.username,
            name: account.name?.split(' ')[0] || account.username.split('@')[0],
            fullName: account.name || account.username,
            role: 'respondent',
            roleName: 'FeedbackTaker',
          };

          setUser(fallbackUser);
        }

        setUserBootstrapped(true);
      }
    };

    if (isAuthenticated && !userBootstrapped && !currentUser && !isInitializing && inProgress === 'none') {
      bootstrapUser();
    }
  }, [isAuthenticated, userBootstrapped, currentUser, isInitializing, inProgress, instance, setUser]);

  if (isInitializing || inProgress !== 'none' || (isAuthenticated && !userBootstrapped && !currentUser)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {inProgress === 'login'
              ? 'Signing in...'
              : isAuthenticated && !userBootstrapped && !currentUser
                ? 'Loading user profile...'
                : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (inProgress === 'none' && !redirectStarted.current) {
      redirectStarted.current = true;
      instance.loginRedirect(loginRequest);
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
